/**
 * @lodex-pro/shared/number-format — the one way a screen shows a number (OPEN-198): numbers of
 * 1,000 or more get thousands separators (3341.25 → "3,341.25", 132558 → "132,558"). Browsers
 * import the `./number-format` subpath.
 *
 * Machine-readable exports (CSV, Excel) keep plain numbers and must NOT use these.
 *
 * Decimal strings from the API ("1234.50") are grouped as text, never through a float, so a
 * money or BBL string is shown exactly. Numbers go through Intl with en-US grouping.
 */

const NUMERAL = /^(-?)(\d+)(\.\d+)?$/;

const intl = new Map<string, Intl.NumberFormat>();
function nf(min: number, max: number): Intl.NumberFormat {
  const key = `${String(min)}:${String(max)}`;
  let f = intl.get(key);
  if (f === undefined) {
    f = new Intl.NumberFormat('en-US', {
      useGrouping: true,
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    });
    intl.set(key, f);
  }
  return f;
}

/** "1234.50" → "1,234.50", exactly; anything that is not a plain numeral comes back unchanged. */
export function groupDigits(numeral: string): string {
  const m = NUMERAL.exec(numeral.trim());
  if (m === null) return numeral;
  const [, sign = '', whole = '', frac = ''] = m;
  return `${sign}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${frac}`;
}

/**
 * A number for a screen. `decimals` fixes the places (rounded); without it a number keeps up to
 * `maxDecimals` (default 2) and a numeral string keeps its own. null / undefined / NaN → "—".
 */
export function formatNumber(
  value: number | string | null | undefined,
  opts: { readonly decimals?: number; readonly maxDecimals?: number } = {},
): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') {
    if (opts.decimals === undefined) return groupDigits(value);
    const n = Number(value);
    return Number.isFinite(n) ? nf(opts.decimals, opts.decimals).format(n) : value;
  }
  if (!Number.isFinite(value)) return '—';
  if (opts.decimals !== undefined) return nf(opts.decimals, opts.decimals).format(value);
  return nf(0, opts.maxDecimals ?? 2).format(value);
}

/** Integer cents → "$1,234.56" ("-$12.00" when negative). Exact: cents are the atom. */
export function formatDollarsFromCents(cents: number): string {
  if (!Number.isInteger(cents)) {
    throw new Error(`formatDollarsFromCents expects integer cents, received ${String(cents)}`);
  }
  const abs = Math.abs(cents);
  const whole = groupDigits(String(Math.floor(abs / 100)));
  return `${cents < 0 ? '-' : ''}$${whole}.${String(abs % 100).padStart(2, '0')}`;
}
